/**
 * useDealsWithSuburbs — composes the Phase 2 deals stream with the Phase 4
 * suburb cache and (where possible) an inspector suggestion.
 *
 * Output shape: each deal gets `regionMatch` (zone + verdict + match type)
 * plus a derived `verdictLabel` for quick filtering. Inspector assignment
 * is computed lazily — first by zone matching against the inspector pool,
 * then by haversine fallback (handled inside `suggestInspectorForDeal`).
 *
 * Resolution is incremental: the deals come back instantly from the CRM
 * adapter cache, and we run suburb lookups in the background. Consumers
 * can render the deal row before its region resolves — they'll get a
 * `loadingRegion: true` flag while it computes.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useDeals } from './useDeals';
import { useTenant } from '../contexts/TenantContext';
import { useInspectors } from './useInspectors';
import { findRegionForAddress, type RegionMatch } from '../utils/regionValidation';
import type { Deal } from '../lib/crm/types';
import type { Inspector } from '../types';

export interface DealWithSuburb {
  deal: Deal;
  regionMatch: RegionMatch | null;
  /** Inspector this deal would be auto-assigned to, if any. */
  assignedInspector: Inspector | null;
  /** Coordinates pulled from the deal's customFields, if Geocoded upstream. */
  lat: number | null;
  lng: number | null;
  loadingRegion: boolean;
}

interface UseDealsWithSuburbsReturn {
  items: DealWithSuburb[];
  loading: boolean;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  error: string | null;
}

/** Pull lat/lng off a deal's customFields if the CRM adapter stashed them there. */
function extractCoords(deal: Deal): { lat: number | null; lng: number | null } {
  const cf = deal.customFields ?? {};
  const candidates: [unknown, unknown][] = [
    [cf.lat, cf.lng],
    [cf.latitude, cf.longitude],
    [cf.geo_lat, cf.geo_lng],
    [cf.coordinates_lat, cf.coordinates_lng],
  ];
  for (const [lat, lng] of candidates) {
    const la = typeof lat === 'number' ? lat : typeof lat === 'string' ? parseFloat(lat) : NaN;
    const lo = typeof lng === 'number' ? lng : typeof lng === 'string' ? parseFloat(lng) : NaN;
    if (Number.isFinite(la) && Number.isFinite(lo)) return { lat: la, lng: lo };
  }
  return { lat: null, lng: null };
}

export function useDealsWithSuburbs(): UseDealsWithSuburbsReturn {
  const { tenant } = useTenant();
  const { items: deals, loading, error, hasMore, loadMore, refresh } = useDeals({ pageSize: 100 });
  const { inspectors } = useInspectors();

  const [regionByDeal, setRegionByDeal] = useState<Map<string, RegionMatch>>(() => new Map());
  const [coordsByDeal, setCoordsByDeal] = useState<Map<string, { lat: number | null; lng: number | null }>>(
    () => new Map(),
  );
  const [resolving, setResolving] = useState<Set<string>>(() => new Set());

  // Cancel in-flight resolves on unmount so we don't setState on a dead tree.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  // Trigger background resolution for any deal we haven't seen yet.
  useEffect(() => {
    if (!tenant) return;
    const todo = deals.filter((d) => !regionByDeal.has(d.id) && !resolving.has(d.id));
    if (todo.length === 0) return;

    setResolving((prev) => {
      const next = new Set(prev);
      for (const d of todo) next.add(d.id);
      return next;
    });

    // Resolve in parallel but batched to avoid hammering Supabase.
    const BATCH = 8;
    void (async () => {
      for (let i = 0; i < todo.length; i += BATCH) {
        const slice = todo.slice(i, i + BATCH);
        const results = await Promise.all(
          slice.map(async (d) => {
            const coords = extractCoords(d);
            const region = await findRegionForAddress(
              d.address ?? '',
              coords.lat ?? undefined,
              coords.lng ?? undefined,
            );
            return [d.id, region, coords] as const;
          }),
        );
        if (!aliveRef.current) return;
        setRegionByDeal((prev) => {
          const next = new Map(prev);
          for (const [id, region] of results) next.set(id, region);
          return next;
        });
        setCoordsByDeal((prev) => {
          const next = new Map(prev);
          for (const [id, , coords] of results) next.set(id, coords);
          return next;
        });
        setResolving((prev) => {
          const next = new Set(prev);
          for (const [id] of results) next.delete(id);
          return next;
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deals, tenant]);

  const items = useMemo<DealWithSuburb[]>(() => {
    return deals.map((deal) => {
      const region = regionByDeal.get(deal.id) ?? null;
      const { lat, lng } = coordsByDeal.get(deal.id) ?? extractCoords(deal);
      const assignedInspector =
        region?.zone != null
          ? inspectors.find((i) => i.region_code === region.zone) ?? null
          : null;
      return {
        deal,
        regionMatch: region,
        assignedInspector,
        lat,
        lng,
        loadingRegion: resolving.has(deal.id),
      };
    });
  }, [deals, regionByDeal, coordsByDeal, resolving, inspectors]);

  return {
    items,
    loading,
    hasMore,
    loadMore,
    refresh,
    error: error?.message ?? null,
  };
}
